import React, { useState } from 'react';
import { Plus, X, Printer } from 'lucide-react';

interface BagCreateModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (stockCardId: string, weight: string) => void;
}

// Stok kartı tipi
interface StockCard {
    id: string;
    code: string;
    name: string;
}

// Mock stok kartları
const stockCards: StockCard[] = [
    { id: '1', code: 'STK001', name: 'Kot Pantolon' },
    { id: '2', code: 'STK002', name: 'T-Shirt' },
    { id: '3', code: 'STK003', name: 'Gömlek' },
];

const BagCreateModal: React.FC<BagCreateModalProps> = ({ isOpen, onClose, onSubmit }) => {
    const [selectedStockCard, setSelectedStockCard] = useState('');
    const [weight, setWeight] = useState('');

    if (!isOpen) return null;

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSubmit(selectedStockCard, weight);
        setSelectedStockCard('');
        setWeight('');
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-8 w-[600px] max-w-[90vw]">
                <div className="flex justify-between items-center mb-8">
                    <h2 className="text-3xl font-semibold text-slate-800">Yeni Çuval Oluştur</h2>
                    <button 
                        onClick={onClose}
                        className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                    >
                        <X className="w-8 h-8 text-slate-500" />
                    </button>
                </div>
                
                <form onSubmit={handleSubmit} className="space-y-8">
                    <div>
                        <label className="block text-2xl text-slate-700 mb-4">
                            Stok Kartı
                        </label>
                        <select
                            value={selectedStockCard}
                            onChange={(e) => setSelectedStockCard(e.target.value)}
                            required
                            className="block w-full text-2xl border-2 border-slate-200 rounded-xl px-6 py-5
                                     focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all
                                     bg-white text-slate-800"
                        >
                            <option value="">Stok Kartı Seçin</option>
                            {stockCards.map(card => (
                                <option key={card.id} value={card.id}>
                                    {card.code} - {card.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-2xl text-slate-700 mb-4">
                            Ağırlık (kg)
                        </label>
                        <input
                            type="number"
                            value={weight}
                            onChange={(e) => setWeight(e.target.value)}
                            required
                            min="0"
                            step="0.1"
                            className="block w-full text-2xl border-2 border-slate-200 rounded-xl px-6 py-5
                                     focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                            placeholder="Ağırlık giriniz"
                        />
                    </div>

                    <button
                        type="submit"
                        className="w-full px-8 py-6 bg-blue-600 text-white rounded-xl 
                                 hover:bg-blue-700 active:bg-blue-800 transition-colors 
                                 text-2xl font-medium"
                    >
                        Onayla
                    </button>
                </form>
            </div>
        </div>
    );
};

interface BagCreatePageProps {
    onNewBagCreate?: () => void;
}

const BagCreatePage: React.FC<BagCreatePageProps> = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [bags, setBags] = useState([
        {
            id: '1',
            bagCode: 'BAG001',
            barcode: '3000000012',
            createdDate: '2025-08-08',
            createdBy: 'Ahmet Yılmaz',
            weight: '12.5'
        },
        {
            id: '2',
            bagCode: 'BAG002',
            barcode: '3000000015',
            createdDate: '2025-08-08',
            createdBy: 'Mehmet Demir',
            weight: '15.3'
        }
    ]);
    const [selectedBags, setSelectedBags] = useState<string[]>([]);

    const handleCreateBag = (stockCardId: string, weight: string) => {
        // Seçilen stok kartını bul
        const selectedCard = stockCards.find(card => card.id === stockCardId);
        if (!selectedCard) return;

        // Yeni çuval kodu ve barkod oluştur
        const lastBag = bags[bags.length - 1];
        const lastBagNumber = parseInt(lastBag.bagCode.replace('BAG', ''));
        const newBagCode = `BAG${String(lastBagNumber + 1).padStart(3, '0')}`;
        const lastBarcodeNumber = parseInt(lastBag.barcode.substring(1));
        const newBarcode = '3' + String(lastBarcodeNumber + 1).padStart(9, '0');

        // Yeni çuval oluştur
        const newBag = {
            id: String(bags.length + 1),
            bagCode: newBagCode,
            barcode: newBarcode,
            createdDate: new Date().toISOString().split('T')[0],
            createdBy: 'Aktif Kullanıcı', // Gerçek kullanıcı adı buraya gelecek
            weight
        };

        // Çuvalları güncelle
        setBags([...bags, newBag]);
    };

    const handlePrint = () => {
        if (selectedBags.length === 0) {
            alert('Lütfen yazdırılacak çuvalları seçin');
            return;
        }
        
        // Seçili çuvalların barkodlarını yazdır
        const selectedBarcodes = bags
            .filter(bag => selectedBags.includes(bag.id))
            .map(bag => bag.barcode);
            
        console.log('Yazdırılacak barkodlar:', selectedBarcodes);
        // TODO: Gerçek yazdırma işlemi burada yapılacak
    };

    return (
        <div className="bg-transparent rounded-xl shadow-none border-0 p-6 max-w-8xl mx-auto">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
                <h1 className="text-3xl font-semibold text-slate-800">M-Çuval Oluşturma</h1>
                <div className="flex gap-4">
                    <button
                        onClick={handlePrint}
                        className="flex items-center justify-center gap-3 px-8 py-6 bg-blue-100 text-blue-700 rounded-xl 
                                hover:bg-blue-200 active:bg-blue-300 transition-colors text-2xl font-medium"
                        disabled={selectedBags.length === 0}
                    >
                        <Printer className="w-8 h-8" />
                        <span>Yazdır</span>
                    </button>
                    <button
                        onClick={() => setIsModalOpen(true)}
                        className="flex items-center justify-center gap-3 px-8 py-6 bg-blue-600 text-white rounded-xl 
                                hover:bg-blue-700 active:bg-blue-800 transition-colors text-2xl font-medium"
                    >
                        <Plus className="w-8 h-8" />
                        <span>Yeni Çuval Oluştur</span>
                    </button>
                </div>
            </div>

            <div className="border-2 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y-2 divide-slate-200">
                        <thead className="bg-slate-50">
                            <tr>
                                <th className="px-8 py-6 text-center text-xl font-medium text-slate-700">
                                    <input
                                        type="checkbox"
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setSelectedBags(bags.map(bag => bag.id));
                                            } else {
                                                setSelectedBags([]);
                                            }
                                        }}
                                        checked={selectedBags.length === bags.length}
                                        className="w-6 h-6 rounded-lg border-2 border-slate-300 text-blue-600
                                                focus:ring-blue-500/20"
                                    />
                                </th>
                                <th className="px-8 py-6 text-left text-xl font-medium text-slate-700">
                                    Çuval Kodu
                                </th>
                                <th className="px-8 py-6 text-left text-xl font-medium text-slate-700">
                                    Barkod
                                </th>
                                <th className="px-8 py-6 text-left text-xl font-medium text-slate-700">
                                    Oluşturan Kişi
                                </th>
                                <th className="px-8 py-6 text-left text-xl font-medium text-slate-700">
                                    Oluşturma Tarihi
                                </th>
                                <th className="px-8 py-6 text-left text-xl font-medium text-slate-700">
                                    Ağırlık (kg)
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y-2 divide-slate-100">
                            {bags.map((bag) => (
                                <tr 
                                    key={bag.id} 
                                    className="hover:bg-slate-50 active:bg-slate-100 transition-colors"
                                >
                                    <td className="px-8 py-8 text-center">
                                        <input
                                            type="checkbox"
                                            checked={selectedBags.includes(bag.id)}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setSelectedBags([...selectedBags, bag.id]);
                                                } else {
                                                    setSelectedBags(selectedBags.filter(id => id !== bag.id));
                                                }
                                            }}
                                            className="w-6 h-6 rounded-lg border-2 border-slate-300 text-blue-600
                                                    focus:ring-blue-500/20"
                                        />
                                    </td>
                                    <td className="px-8 py-8 whitespace-nowrap text-2xl font-medium text-slate-900">
                                        {bag.bagCode}
                                    </td>
                                    <td className="px-8 py-8 whitespace-nowrap text-2xl font-mono text-slate-700">
                                        {bag.barcode}
                                    </td>
                                    <td className="px-8 py-8 whitespace-nowrap text-2xl text-slate-700">
                                        {bag.createdBy}
                                    </td>
                                    <td className="px-8 py-8 whitespace-nowrap text-2xl text-slate-700">
                                        {bag.createdDate}
                                    </td>
                                    <td className="px-8 py-8 whitespace-nowrap text-2xl text-slate-700">
                                        {bag.weight}
                                    </td>
                                </tr>
                            ))}
                            {bags.length === 0 && (
                                <tr>
                                    <td colSpan={4} className="px-8 py-10 text-center text-2xl text-slate-500">
                                        Henüz çuval oluşturulmamış
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <BagCreateModal 
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSubmit={handleCreateBag}
            />
        </div>
    );
};

export default BagCreatePage;
